import * as React from 'react'
import { connect } from 'react-redux'
import { useRouter } from 'next/router'
import { fantasyQuizCopy, hunkQuizCopy, tropeQuizCopy } from '@components/quiz/quizCopy'
import {
  AnswersWrapper,
  AnswerText,
  AuthFormWrapper,
  ImgWrapper,
  ProgressBar,
  QuestionText,
  QuestionText2,
  QuizBlock,
  QuizImg,
  QuizSubtext,
  QuizTextWrapper,
  SingleAnswerWrapper
} from '@components/quiz/quizStyles'
import type {
  AnswerType,
  QuizQuestionType,
  QuizResultSlidesType,
  QuizResultsType,
  QuizType,
  TieBreakersQuestionType,
  UserScoreCardConversionQuizResultsType
} from '../../../flowTypes'
import { setQuizResult } from '../../../actions/userActions'
import { events } from '../../../services/analytics'
import QuizResults from '@components/quiz/flow1/QuizResults'
import AuthForm from '@components/auth/AuthForm'
import type { Dispatch } from 'redux'
import type { AppStateType } from '../../../reducers/appstate'
import {
  parseQuery, quizResult
} from './helpers'

export type PropsType = {
  quiz: QuizQuestionType[],
  tieBreakers: TieBreakersQuestionType[],
  results: QuizResultsType,
  resultSlides: QuizResultSlidesType[],
  resultHeader: string,
  isSignedIn: boolean,
  existingResult?: string,
  existingGenderPreference?: string,
  quizType: QuizType
}
export type DispatchPropsType = {
  setQuizResult: (
    name: string,
    id: string,
    genderPreference: string,
    results: UserScoreCardConversionQuizResultsType,
    quizType: QuizType
  ) => void
}

export type StateType = {
  currentQuestionIndex: number,
  currentQuestion: QuizQuestionType | TieBreakersQuestionType,
  answers: string[][],
  eventInfo: {
    question: string,
    answer: string
  }[],
  showProgressBar: boolean,
  transitioning: boolean,
  showCreateAccount: boolean,
  showResultsScreen: boolean,
  finalResult: string | null,
  genderPreference: string | null,
  socialAuth: boolean
}

function Quiz (props: PropsType & DispatchPropsType): React.ReactNode {
  const router = useRouter()
  const searchParams = new URLSearchParams(router.asPath.split(/\?/)[1])
  const query: Record<string, any> = {}
  for (const [key, value] of searchParams) {
    query[key] = value
  }
  // static site generation won't pull the query object for us
  router.query = query
  const getStateFromQuery = parseQuery(query, props.quiz, props.tieBreakers)
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState<number>(getStateFromQuery.currentQuestionIndex)
  const [currentQuestion, setCurrentQuestion] = React.useState<QuizQuestionType | TieBreakersQuestionType>(getStateFromQuery.currentQuestion)
  const [answers, setAnswers] = React.useState<string[][]>(getStateFromQuery.answers)
  const [eventInfo, setEventInfo] = React.useState<{
    question: string,
    answer: string
  }[]>(getStateFromQuery.eventInfo)
  const [showProgressBar, setShowProgressBar] = React.useState<boolean>(getStateFromQuery.showProgressBar)
  const [transitioning, setTransitioning] = React.useState<boolean>(getStateFromQuery.transitioning)
  const [showCreateAccount, setShowCreateAccount] = React.useState<boolean>(getStateFromQuery.showCreateAccount)
  const [showResultsScreen, setShowResultsScreen] = React.useState<boolean>(getStateFromQuery.showResultsScreen)
  const [finalResult, setFinalResult] = React.useState<string | null>(getStateFromQuery.finalResult)
  const [genderPreference, setGenderPreference] = React.useState<string | null>(getStateFromQuery.genderPreference)
  // eslint-disable-next-line no-unused-vars
  const [socialAuth] = React.useState<boolean>(getStateFromQuery.socialAuth)
  // Used to up the query as we go, in case the user refreshes or a redirect happens from social auth login
  const updateQueryParams = (name: string, value: string): void => {
    router.push({ query: { ...router.query, [name]: value } })
  }
  React.useEffect((): void => {
    if (props.isSignedIn) {
      setShowCreateAccount(false)
    }
  }, [props.isSignedIn])

  React.useEffect((): void => {
    // setCurrentQuestionIndex(0)
    props.quiz.forEach((question: QuizQuestionType): void => {
      new Image().src = question.icon
    })

    if (props.existingResult && props.isSignedIn) {
      setFinalResult(props.existingResult)
      setShowResultsScreen(true)
      setShowProgressBar(false)
      props.setQuizResult(
        props.existingResult || '',
        props.results[props.existingResult || ''].id,
        genderPreference || props.existingGenderPreference || '',
        eventInfo,
        props.quizType
      )
    }
    setTransitioning(false)
  }, [])

  React.useEffect((): void => {
    if (currentQuestionIndex + 1 >= props.quiz.length) {
      checkForTiebreakers()
    }
  }, [answers])

  const nextQuestion = (): void => {
    let submittedAnswerIndex: number = 0
    currentQuestion.answers.forEach((el: AnswerType, index: number): void => {
      if (
        (el || {}).outcomes.length > 0 &&
        typeof el !== 'string' &&
        answers.length &&
        answers[answers.length - 1].length > 1 &&
        answers[answers.length - 1].every(
          (submittedAnswer: string): boolean =>
            el.outcomes.indexOf(submittedAnswer) > -1
        )
      ) {
        submittedAnswerIndex = index
      }
    })

    events.quiz_answer_selected({
      currentQuestion: currentQuestionIndex + 1,
      total: props.quiz.length,
      questionText: currentQuestion.questionText,
      answer: (currentQuestion.answers[submittedAnswerIndex] || {}).text,
      quizType: props.quizType
    })
    if (currentQuestionIndex + 1 < props.quiz.length) {
      initTransition((): void => {
        setCurrentQuestionIndex(currentQuestionIndex + 1)
        setCurrentQuestion(props.quiz[currentQuestionIndex + 1])
      })
    }
  }

  const checkForTiebreakers = (): void => {
    const winners = quizResult(props.results, answers, genderPreference || props.existingGenderPreference || 'Both')

    if (winners.length > 1) {
      const tieBreakerQuestions = props.tieBreakers
      const tieBreaker: TieBreakersQuestionType[] = []
      tieBreakerQuestions.forEach(
        (question: TieBreakersQuestionType): void => {
          const isSame = (winners.length === question.vs.length) && winners.every((element: string, _index: number): boolean => {
            return question.vs.includes(element)
          })

          if (isSame) {
            tieBreaker.push(question)
          }
        }
      )

      if (tieBreaker.length > 0) {
        initTransition((): void => {
          setCurrentQuestion(tieBreaker[0])
          setCurrentQuestionIndex(currentQuestionIndex + 1)
        })
      } else {
        let tieBreakers = Object.keys(hunkQuizCopy.results)
        if (props.quizType === 'trope-quiz') {
          tieBreakers = Object.keys(tropeQuizCopy.results)
        } else if (props.quizType === 'fantasy-quiz') {
          tieBreakers = Object.keys(fantasyQuizCopy.results)
        }

        const rankedResults = tieBreakers.filter(
          (item: string): boolean => {
            return winners.includes(item)
          }
        )
        if (!props.isSignedIn && !socialAuth) {
          setShowCreateAccount(true)
        }
        setFinalResult(rankedResults[0])
        setShowResultsScreen(true)
        props.setQuizResult(
          rankedResults[0],
          props.results[rankedResults[0]].id,
          genderPreference || props.existingGenderPreference || '',
          eventInfo,
          props.quizType
        )
      }
      setShowProgressBar(false)
    } else {
      if (!props.isSignedIn && !socialAuth) {
        setShowCreateAccount(true)
      }
      setShowResultsScreen(true)
      setShowProgressBar(false)
      setFinalResult(winners[0])
      props.setQuizResult(
        winners[0],
        props.results[winners[0]].id,
        genderPreference || props.existingGenderPreference || '',
        eventInfo,
        props.quizType
      )
    }
  }

  const initTransition = (callback: () => void): void => {
    setTransitioning(true)
    setTimeout((): void => {
      callback()
    }, 500)
    setTimeout((): void => {
      setTransitioning(false)
    }, 750)
  }

  const updateSelectedOption = (currentIndex: number): void => {
    if (currentQuestion.genderPref) {
      if (currentQuestion.answers[currentIndex].genderPrefText) {
        setGenderPreference(currentQuestion.answers[currentIndex].genderPrefText)
      }
    }
    const answer = currentQuestion.answers[currentIndex]
    const outcomes = answer.outcomes
    const newAnswers = [...answers]
    newAnswers[currentQuestionIndex] = outcomes
    updateQueryParams(`${currentQuestion.type ? 't' : 'q'}${currentQuestion.index}`, `${answer.index}`)
    setAnswers(newAnswers)
    setEventInfo([
      ...eventInfo,
      {
        question: currentQuestion.questionText,
        answer: currentQuestion.answers[currentIndex].text
      }
    ])
  }
  return (
    <>
      <div style={{ position: 'fixed', width: '100%' }}>
        <ProgressBar
          className='quiz-progres-bar'
          progress={((currentQuestionIndex + 1) / props.quiz.length) * 100}
          isSignedIn={props.isSignedIn}
          showProgressBar={showProgressBar}
        />
      </div>

      {showCreateAccount && showResultsScreen && !socialAuth && (
        <QuizBlock isSignedIn={props.isSignedIn} className='quiz-block'>
          <QuizTextWrapper transitioning={transitioning}>
            <QuestionText2>Your match is waiting...</QuestionText2>
            <QuizSubtext>
              See your results and access hundreds of sexy stories, sleep scenes, and
              wellness sessions.
            </QuizSubtext>
            <AuthFormWrapper>
              <AuthForm theme={'white'} />
            </AuthFormWrapper>
          </QuizTextWrapper>
        </QuizBlock>
      )}

      {!showCreateAccount && showResultsScreen && finalResult && (
        <QuizResults
          result={props.results[finalResult]}
          resultHeader={props.resultHeader}
          existingResult={finalResult}
          triggerConversionEvent={(): void =>
            props.setQuizResult(
              finalResult,
              props.results[finalResult].id,
              genderPreference || props.existingGenderPreference || '',
              eventInfo,
              props.quizType
            )
          }
        />

      )}
      {currentQuestion &&
        currentQuestion.icon &&
        !showCreateAccount &&
        !showResultsScreen && (
        <QuizBlock isSignedIn={props.isSignedIn} className='quiz-block'>
          <div className='question-wrapper'>
            <ImgWrapper transitioning={transitioning}>
              <QuizImg
                src={currentQuestion.icon}
                alt={currentQuestion.iconAlt || null}
              />
            </ImgWrapper>
            <QuizTextWrapper transitioning={transitioning}>
              <QuestionText>{currentQuestion.questionText}</QuestionText>
            </QuizTextWrapper>
            <AnswersWrapper
              transitioning={transitioning}
              isSignedIn={props.isSignedIn}
            >
              {currentQuestion &&
                  currentQuestion.answers.map(
                    (answer: AnswerType, _i: number): any => {
                      return (
                        <SingleAnswerWrapper
                          key={`question-set-${answer.index}-${currentQuestionIndex}`}
                        >
                          <input
                            id={`question-${answer.index}-${currentQuestionIndex}`}
                            type='radio'
                            name={`quiz-question-${currentQuestionIndex}`}
                            onChange={(): void => {
                              updateSelectedOption(answer.index)
                              nextQuestion()
                            }}
                            value={answer.text}
                          />
                          <label
                            htmlFor={`question-${answer.index}-${currentQuestionIndex}`}
                          >
                            <AnswerText>{answer.text}</AnswerText>
                          </label>
                        </SingleAnswerWrapper>
                      )
                    }
                  )}
            </AnswersWrapper>
          </div>
        </QuizBlock>
      )}
    </>
  )
}

type OwnPropsType = {
  isSignedIn: boolean,
  existingResult?: string,
  existingGenderPreference?: string,
  quizType: QuizType
}

const mapStateToProps = (state: AppStateType, ownProps: OwnPropsType): PropsType => {
  let { quiz, tieBreakers, results, resultSlides, resultHeader } = hunkQuizCopy

  if (ownProps.quizType === 'trope-quiz') {
    quiz = tropeQuizCopy.quiz
    tieBreakers = tropeQuizCopy.tieBreakers
    results = tropeQuizCopy.results
    resultSlides = tropeQuizCopy.resultSlides
    resultHeader = tropeQuizCopy.resultHeader
  } else if (ownProps.quizType === 'fantasy-quiz') {
    quiz = fantasyQuizCopy.quiz
    tieBreakers = fantasyQuizCopy.tieBreakers
    results = fantasyQuizCopy.results
    resultSlides = fantasyQuizCopy.resultSlides
    resultHeader = fantasyQuizCopy.resultHeader
  }

  return {
    quiz,
    tieBreakers,
    results,
    resultSlides,
    resultHeader,
    isSignedIn: ownProps.isSignedIn,
    existingResult: ownProps.existingResult,
    existingGenderPreference: ownProps.existingGenderPreference,
    quizType: ownProps.quizType
  } as PropsType
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchPropsType => {
  return {
    setQuizResult: (
      name: string,
      id: string,
      genderPreference: string,
      results: UserScoreCardConversionQuizResultsType,
      quizType: QuizType
    ): void => dispatch(setQuizResult(name, id, genderPreference, results, quizType))
  } as DispatchPropsType
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Quiz)
